import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import getStateManager from '../StateManager.js';
import { IconButton } from '@material-ui/core';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Typography from '@material-ui/core/Typography';

import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { EnableDisableSelect } from '../FormWidgets/Select.js';
import Desc from '../FormWidgets/Description';
import { HandleAction } from '../Utils/utils.js';

import { settingControls, useSavedSettings } from './settingFunctions';
import { SettingActions } from '../components';

export default function LightsSettings({ controller, parent, ...other }) {
  let { tab_id } = useParams();
  const stateManager = getStateManager();
  const [values, setValues] = useState(parent.settings[tab_id]);
  console.log('websocket update_setpoints 1', parent, tab_id);
  const controls = settingControls(controller, parent, tab_id, values, setValues);
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(parent, controls);

  const read_access_climate = controller.access_rights.climate.read;
  const read_access_co2 = controller.access_rights.co2.read;
  const write_access = controller.access_rights.lights.write;

  const callAction = (type, value) => {
    if (controller.connection.status !== 'bad')
      HandleAction(stateManager.wsManager, controller, type, value);
  };

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      {write_access && (
        <SettingActions onReset={controls.resetSettings} onSave={controls.saveSettings} />
      )}

      <SettingGroupGrid controller={controller} id={'lights'}>
        <ShowSetting
          id="lights_max"
          controller={controller}
          cur_value={values.lights_max}
          controller_value={parent.settings.lights.lights_max}
          min={values.lights_min}
          controls={controls}
          disabled={!write_access}
        />
        <ShowSetting
          id="lights_min"
          controller={controller}
          cur_value={values.lights_min}
          controller_value={parent.settings.lights.lights_min}
          max={values.lights_max}
          controls={controls}
          disabled={!write_access}
        />
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'lights_height_adjustment_hl'}>
        <ValueBox controller={controller} id={'lights_height_adjustment_single'}>
          <Typography gutterBottom>
            <Desc id="lights_height_adjustment_single" />
          </Typography>
          <div style={{ display: 'flex', gap: 21 }}>
            {[1, 2, 3, 4].map((num) => (
              <div
                key={num}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <IconButton onClick={() => callAction(`action_lights_height_adjustment_up_${num}`)}>
                  <KeyboardArrowUpIcon />
                </IconButton>
                <IconButton
                  onClick={() => callAction(`action_lights_height_adjustment_down_${num}`)}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
                <Typography variant="caption" color="Primary">
                  <Desc id={`${num}`} />
                </Typography>
              </div>
            ))}
          </div>
        </ValueBox>
        <ValueBox controller={controller} id={'lights_height_adjustment_global'}>
          <Typography gutterBottom>
            <Desc id="lights_height_adjustment_global" />
          </Typography>
          <IconButton onClick={() => callAction('action_lights_height_adjustment_up_0')}>
            <KeyboardArrowUpIcon />
          </IconButton>
          <br />
          <IconButton onClick={() => callAction('action_lights_height_adjustment_down_0')}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </ValueBox>
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'lights_sunrise_duration'}>
        <ShowSetting
          id="light_sunrise_duration"
          controller={controller}
          cur_value={values.light_sunrise_duration}
          controller_value={parent.settings.lights.light_sunrise_duration}
          controls={controls}
          disabled={!write_access}
        />
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'over_temperature_hl'}>
        <ValueBox controller={controller} id={'lights_dim_over_temp_enable'}>
          <Typography gutterBottom>
            <Desc id="lights_dim_over_temp_enable" />
          </Typography>
          <EnableDisableSelect
            value={values.lights_dim_over_temp_enable}
            name="lights_dim_over_temp_enable"
            onChange={(event, value) => {
              controls.setValue('lights_dim_over_temp_enable', event.target.value);
            }}
            disabled={!write_access}
          />
        </ValueBox>
        <ShowSetting
          id="lights_dim_over_temp_temp_day"
          controller={controller}
          cur_value={values.lights_dim_over_temp_temp_day}
          controller_value={parent.settings.lights.lights_dim_over_temp_temp_day}
          min={read_access_climate ? parent.settings.climate.temp_sp_day / 10 + 1 : 0}
          controls={controls}
          disabled={!write_access}
          type="float"
        />
        <ShowSetting
          id="lights_dim_over_temp_temp_co2"
          controller={controller}
          cur_value={values.lights_dim_over_temp_temp_co2}
          controller_value={parent.settings.lights.lights_dim_over_temp_temp_co2}
          min={read_access_co2 ? parent.settings.co2.temp_sp_co2 / 10 + 1 : 0}
          controls={controls}
          disabled={!write_access}
          type="float"
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_ph1"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_ph1}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_ph1}
          min={values.lights_min - 1}
          max={values.lights_max}
          controls={controls}
          disabled={!write_access}
          off_at={values.lights_min - 1}
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_ph2"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_ph2}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_ph2}
          min={values.lights_min - 1}
          max={values.lights_dim_over_temp_reduct_ph1}
          controls={controls}
          disabled={!write_access}
          off_at={values.lights_min - 1}
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_ph3"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_ph3}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_ph3}
          min={values.lights_min - 1}
          max={values.lights_dim_over_temp_reduct_ph2}
          controls={controls}
          disabled={!write_access}
          off_at={values.lights_min - 1}
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_wait_minute"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_wait_minute}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_wait_minute}
          controls={controls}
          disabled={!write_access}
        />
      </SettingGroupGrid>

      {write_access && (
        <SettingActions onReset={controls.resetSettings} onSave={controls.saveSettings} />
      )}
    </React.Fragment>
  );
}
